import { type QueryKey } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { releasesGridConfig } from '@/features/releases/grid/gridConfig';
import { buildTagColorByName } from '@/features/shared/tags';
import { useTagOptions } from '@/shared/hooks';
import { useIsAuthenticated } from '@/shared/hooks/auth';
import { buildReleasesColumns } from './releasesColumns';
import { useReleasesTableRowActions } from './useReleasesTableRowActions';
import type { ReleasesTableProps } from '@/features/releases/components/ReleasesTable.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef } from '@/ui/primitives';
import type { QueryResult, ReleaseListItemDto } from '@flowqa/shared';

type ReleasesTableControllerInput = Pick<
  ReleasesTableProps,
  | 'initialFilterModel'
  | 'initialSortModel'
  | 'persistKey'
  | 'queryFn'
  | 'queryKey'
  | 'syncStateToUrl'
>;

type ReleasesTableController = {
  columns: Array<GridColDef<ReleaseListItemDto>>;
  deleteLoading: boolean;
  deleteTarget: ReturnType<typeof useReleasesTableRowActions>['deleteTarget'];
  gridConfig: ReturnType<typeof releasesGridConfig>;
  isAuthenticated: boolean;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<ReleaseListItemDto>>;
  queryKey: QueryKey;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

export const useReleasesTableController = ({
  initialFilterModel,
  initialSortModel,
  persistKey,
  queryFn,
  queryKey,
  syncStateToUrl = false,
}: ReleasesTableControllerInput): ReleasesTableController => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useReleasesTableRowActions({ selectedProjectId });
  const { tagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: isAuthenticated,
  });
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns = useMemo(
    () =>
      buildReleasesColumns({
        onProjectSelect: setSelectedProjectId,
        onDeleteRow,
        tagColorByName,
        deleteRowPendingId,
        onUpdateRowTagsColor,
        updateRowTagsColorPendingId,
      }),
    [
      deleteRowPendingId,
      onDeleteRow,
      onUpdateRowTagsColor,
      setSelectedProjectId,
      tagColorByName,
      updateRowTagsColorPendingId,
    ],
  );
  const gridConfig = releasesGridConfig({
    orgId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'releases-table',
    initialSortModel,
    initialFilterModel,
  });

  return {
    columns,
    deleteLoading,
    deleteTarget,
    gridConfig,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: queryFn ?? gridConfig.queryFn,
    queryKey: queryKey ?? gridConfig.queryKey,
    selectedOrganisationId,
    selectedProjectId,
  };
};
